<script setup lang="ts">
import {
  FindCompaniesDocument,
  useCreateCompanyInlineMutation,
  type FindCompaniesQuery,
  type CompanyListItemFragment,
} from "../graphql/types"
import { useAsyncEntitySearch } from "../forms/composables/useAsyncEntitySearch"
import AsyncBadgeCombobox from "../forms/components/AsyncBadgeCombobox.vue"
import { notifyErrors } from "../notifications"

const modelValue = defineModel<CompanyListItemFragment[]>({ default: () => [] })

withDefaults(defineProps<{ disabled?: boolean; creatable?: boolean }>(), { disabled: false, creatable: true })

const { items, loading, hasMore, search, loadMore } = useAsyncEntitySearch<FindCompaniesQuery, CompanyListItemFragment>({
  queryDocument: FindCompaniesDocument,
  extract: (r) => r.findCompanies
})

// Inline creation keeps the editor in the article flow: only the name is captured here,
// logo/website/etc. are completed later in the company editor.
const { mutate: createCompany } = useCreateCompanyInlineMutation({})

async function onCreate(name: string): Promise<void> {
  const result = await createCompany({ create: { name } })
  notifyErrors(result?.errors)
  const created = result?.data?.createCompany
  if (created && !modelValue.value.some((c) => c.uuid === created.uuid)) {
    modelValue.value = [...modelValue.value, created]
  }
}
</script>

<template>
  <AsyncBadgeCombobox
    v-model="modelValue"
    :items="items"
    :loading="loading"
    :has-more="hasMore"
    :search="search"
    :label-fn="(c) => c.name"
    placeholder="Unternehmen suchen..."
    :disabled="disabled"
    :creatable="creatable"
    :create-label-fn="(q: string) => `Unternehmen „${q}“ anlegen`"
    @create="onCreate"
    @load-more="loadMore"
  >
    <template #badge="{ item }">
      <img
        v-if="item.logo?.previewUrl"
        :src="item.logo.previewUrl"
        alt=""
        class="h-4 w-auto"
      />
      <span>{{ item.name }}<template v-if="item.acronym"> ({{ item.acronym }})</template></span>
    </template>
    <template #option="{ item }">
      <img
        v-if="item.logo?.previewUrl"
        :src="item.logo.previewUrl"
        alt=""
        class="w-8 aspect-square object-contain flex-shrink-0"
      />
      <span class="flex-1">
        {{ item.name }}<template v-if="item.acronym"> ({{ item.acronym }})</template>
      </span>
    </template>
  </AsyncBadgeCombobox>
</template>
